import { drillTrailSchema, TRAFFIC_LIGHT_LABELS, type TrafficLight } from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { drillHref, type DrillPath } from './drill';

const pct = (v: number | null) =>
  v === null ? 'sem dado' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

type Crumb = {
  key: string;
  label: string;
  performance: number | null;
  light: TrafficLight;
  to: string | null;
};

/**
 * Breadcrumb do drill-down (T-144b, RF-37, PRD §11.24; DEC-058): o caminho empresa → perspectiva → objetivo → KPI →
 * unidade até a tela atual, com a performance de cada nível no mês; cada nível acima leva à sua tela, com o contexto.
 */
export function DrillTrail({ organizationId, path }: { organizationId: string; path: DrillPath }) {
  const { client } = useAuth();
  const can = useCan();
  const user = useCurrentUser();
  const period = path.year && path.month ? { year: String(path.year), month: String(path.month) } : {};
  const trail = useQuery({
    queryKey: [
      'drill-trail',
      path.projectId,
      path.perspectiveId ?? null,
      path.objectiveId ?? null,
      path.kpiId ?? null,
      path.businessUnitId ?? null,
      path.year ?? null,
      path.month ?? null,
    ],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${path.projectId}/drill-down/trail`, {
        query: {
          ...(path.perspectiveId ? { perspectiveId: path.perspectiveId } : {}),
          ...(path.objectiveId ? { objectiveId: path.objectiveId } : {}),
          ...(path.kpiId ? { kpiId: path.kpiId } : {}),
          ...(path.businessUnitId ? { businessUnitId: path.businessUnitId } : {}),
          ...period,
        },
        schema: drillTrailSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
    // A trilha vem da Visão Geral da empresa: sem ela, a tela segue sem o breadcrumb.
    enabled: can('visao-geral', 'view'),
  });

  if (!can('visao-geral', 'view')) return null;

  if (trail.isPending) {
    return (
      <p className="text-xs text-muted-foreground" aria-live="polite">
        Carregando o caminho…
      </p>
    );
  }
  if (trail.isError && !trail.data) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" role="status">
        <span>Não foi possível montar o caminho: {errorMessage(trail.error)}</span>
        <Button variant="ghost" size="sm" onClick={() => void trail.refetch()} disabled={trail.isFetching}>
          Recarregar o caminho
        </Button>
      </div>
    );
  }
  const t = trail.data;
  // O caminho segue com o que a trilha confirmou (perspectiva e objetivo podem vir do nível de baixo).
  const at: DrillPath = {
    projectId: t.project.id,
    perspectiveId: t.perspective?.id ?? null,
    objectiveId: t.objective?.id ?? null,
    kpiId: t.kpi?.id ?? null,
    businessUnitId: t.unit?.id ?? null,
    year: t.year,
    month: t.month,
  };
  const fluxus = user.organization.isFluxus;
  const crumbs: Crumb[] = [
    {
      key: 'company',
      label: 'Empresa',
      performance: t.company.performance,
      light: t.company.light,
      to: (fluxus ? can('dashboard-executivo', 'view') : can('visao-geral', 'view'))
        ? drillHref.company(organizationId, at, fluxus)
        : null,
    },
  ];
  if (t.perspective) {
    crumbs.push({
      key: 'perspective',
      label: t.perspective.name,
      performance: t.perspective.performance,
      light: t.perspective.light,
      to: can('dashboard-perspectiva', 'view') ? drillHref.perspective(organizationId, at) : null,
    });
  }
  if (t.objective) {
    crumbs.push({
      key: 'objective',
      label: t.objective.name,
      performance: t.objective.performance,
      light: t.objective.light,
      to: can('objetivos', 'view')
        ? drillHref.objective(organizationId, { ...at, objectiveId: t.objective.id })
        : null,
    });
  }
  if (t.kpi) {
    crumbs.push({
      key: 'kpi',
      label: `${t.kpi.code} · ${t.kpi.name}`,
      performance: t.kpi.performance,
      light: t.kpi.light,
      to: can('metas-resultados', 'view')
        ? drillHref.kpi(organizationId, { ...at, businessUnitId: null })
        : null,
    });
  }
  if (t.unit) {
    crumbs.push({
      key: 'unit',
      label: t.unit.name,
      performance: t.unit.performance,
      light: t.unit.light,
      to: drillHref.unit(organizationId, at),
    });
  }

  return (
    <nav aria-label="Caminho até a causa" className="min-w-0">
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-1.5 text-sm">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          const content = (
            <>
              <span className="break-words">{c.label}</span>
              <StatusBadge status={c.light} label={pct(c.performance)} />
              <span className="sr-only">, {TRAFFIC_LIGHT_LABELS[c.light]}</span>
            </>
          );
          return (
            <li key={c.key} className="inline-flex min-w-0 items-center gap-1">
              {last || !c.to ? (
                <span
                  aria-current={last ? 'page' : undefined}
                  className={`inline-flex min-w-0 flex-wrap items-center gap-1.5 ${last ? 'font-semibold' : 'text-muted-foreground'}`}
                >
                  {content}
                </span>
              ) : (
                <Link
                  to={c.to}
                  className="inline-flex min-h-8 min-w-0 max-md:min-h-11 flex-wrap items-center gap-1.5 rounded text-brand-strong underline-offset-2 hover:underline dark:text-brand"
                >
                  {content}
                </Link>
              )}
              {last ? null : <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />}
            </li>
          );
        })}
      </ol>
      <p className="sr-only">
        Números de {String(t.month).padStart(2, '0')}/{t.year}.
      </p>
    </nav>
  );
}
